import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Fab from '@mui/material/Fab';
import IconButton from '@mui/material/IconButton';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import { BackIcon } from '../../components/AppHeader';
import { grey } from '@mui/material/colors';
import { summarizeScore } from '../../utils/scoring';
import ScoreForm from '../../components/ScoreForm';
import PlusMinus from '../../components/PlusMinus';

const LS_KEY = '__app_quickscore';

export default function QuickScoreStage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { matchIndex: matchIndexStr, stageIndex: stageIndexStr } = useParams();
  const mIdx = parseInt(matchIndexStr, 10);
  const sIdx = parseInt(stageIndexStr, 10);

  const [data, setData] = useState(() => {
    const saved = localStorage.getItem(LS_KEY);
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    if (data !== null) {
      localStorage.setItem(LS_KEY, JSON.stringify(data));
    }
  }, [data]);

  const matchData = data && data[mIdx];
  const stageData = matchData && (matchData.stages || [])[sIdx];
  const isNewStage = !stageData || !stageData.targets;

  // Edit mode state
  const [editMode, setEditMode] = useState(isNewStage);
  const [stageName, setStageName] = useState(stageData?.title || `Stage ${sIdx + 1}`);
  const [paper, setPaper] = useState(stageData?.targets?.paper || 0);
  const [poppers, setPoppers] = useState(stageData?.targets?.poppers || 0);

  // Scoring mode state
  const [scoringMode, setScoringMode] = useState(false);
  const [shooterName, setShooterName] = useState('');
  const [scoreData, setScoreData] = useState({});
  const [scorePenalties, setScorePenalties] = useState(0);

  // Current stage paper/poppers for scoring
  const curPaper = stageData?.targets?.paper || 0;
  const curPoppers = stageData?.targets?.poppers || 0;

  // Build stage object for ScoreForm
  const stageForForm = useMemo(() => ({
    rounds: curPaper * 2 + curPoppers,
    targets: { paper: curPaper, poppers: curPoppers, plates: 0, 'paper-no-shoots': 100 },
  }), [curPaper, curPoppers]);

  // Live preview
  const liveResult = useMemo(() => {
    if (!scoringMode || !stageData) return null;
    try {
      return summarizeScore({
        score: {
          ...scoreData,
          penaltiesByType: { general: scorePenalties },
        },
        powerFactor: 'minor',
        stage: stageForForm,
      });
    } catch {
      return null;
    }
  }, [scoringMode, scoreData, scorePenalties, stageForForm, stageData]);

  if (!matchData) {
    navigate('/app/quickscore', { replace: true });
    return null;
  }

  // --- Save stage (edit mode) ---
  function onSaveStage() {
    const newData = JSON.parse(JSON.stringify(data));
    const stage = {
      id: stageData?.id || uuidv4(),
      title: stageName || `Stage ${sIdx + 1}`,
      created: stageData?.created || Date.now(),
      targets: { paper, poppers },
      scores: stageData?.scores || [],
    };
    if (!newData[mIdx].stages) newData[mIdx].stages = [];
    if (sIdx < newData[mIdx].stages.length) {
      newData[mIdx].stages[sIdx] = { ...newData[mIdx].stages[sIdx], ...stage };
    } else {
      newData[mIdx].stages.push(stage);
    }
    localStorage.setItem(LS_KEY, JSON.stringify(newData));
    setData(newData);
    setEditMode(false);
  }

  // --- Delete stage ---
  function onDeleteStage() {
    const newData = JSON.parse(JSON.stringify(data));
    newData[mIdx].stages.splice(sIdx, 1);
    localStorage.setItem(LS_KEY, JSON.stringify(newData));
    setData(newData);
    navigate('/app/quickscore/' + mIdx);
  }

  // --- Enter scoring mode ---
  function onNewScore() {
    const numScores = (stageData?.scores || []).length;
    setShooterName(`Shooter ${numScores + 1}`);
    setScoreData({ scorePerTarget: [], poppers: curPoppers, time: 0, 'no-shoots': 0 });
    setScorePenalties(0);
    setScoringMode(true);
  }

  // --- Save score ---
  function onSaveScore() {
    const newData = JSON.parse(JSON.stringify(data));
    newData[mIdx].stages[sIdx].scores.push({
      id: uuidv4(),
      created: Date.now(),
      title: shooterName || `Shooter ${stageData.scores.length + 1}`,
      score: {
        ...scoreData,
        penaltiesByType: { general: scorePenalties },
      },
    });
    localStorage.setItem(LS_KEY, JSON.stringify(newData));
    setData(newData);
    setScoringMode(false);
  }

  // --- Delete score ---
  function onDeleteScore(scoreIdx) {
    const newData = JSON.parse(JSON.stringify(data));
    newData[mIdx].stages[sIdx].scores.splice(scoreIdx, 1);
    localStorage.setItem(LS_KEY, JSON.stringify(newData));
    setData(newData);
  }

  // ==================== EDIT MODE ====================
  if (editMode) {
    return (
      <Box sx={{ pb: 4, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        {/* Hero header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
          color: 'white',
          px: 2, pt: 1.5, pb: 3,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <IconButton
              onClick={() => isNewStage ? navigate('/app/quickscore/' + mIdx) : setEditMode(false)}
              sx={{ color: 'white', mr: 1 }}
            >
              <BackIcon />
            </IconButton>
            <Box>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                {matchData.title}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {t('app.qsStageDetails')}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ px: 1.5, mt: -1.5 }}>
          <Paper sx={{ textAlign: 'center', p: 3, borderRadius: 2.5 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>{t('app.qsStageName')}</Typography>
            <TextField
              value={stageName}
              onChange={(e) => setStageName(e.target.value)}
              size="small"
              fullWidth
              sx={{ mb: 3 }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
              <PlusMinus
                label={t('app.qsTargets')}
                value={paper}
                onChange={setPaper}
                min={0} size="large"
              />
              <PlusMinus
                label={t('app.qsPoppers')}
                value={poppers}
                onChange={setPoppers}
                min={0} size="large"
              />
            </Box>

            <Button
              variant="contained"
              onClick={onSaveStage}
              disabled={paper === 0 && poppers === 0}
              fullWidth
              sx={{ mt: 3, borderRadius: 2.5, py: 1.5, textTransform: 'none', fontWeight: 600 }}
            >
              {t('common.save')}
            </Button>
          </Paper>
        </Box>
      </Box>
    );
  }

  // ==================== SCORING MODE ====================
  if (scoringMode) {
    return (
      <Box sx={{ pb: 4 }}>
        {/* Top bar */}
        <Box sx={{ minHeight: 45, bgcolor: 'white', display: 'flex', alignItems: 'center', px: 1 }}>
          <IconButton onClick={() => setScoringMode(false)}>
            <BackIcon />
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, ml: 1 }}>
            {t('app.qsNewScore')}
          </Typography>
        </Box>

        <Box sx={{ mx: 1.5, mt: 1 }}>
          {/* Shooter name */}
          <Paper sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>{t('app.qsShooterName')}</Typography>
            <TextField
              value={shooterName}
              onChange={(e) => setShooterName(e.target.value)}
              size="small"
              fullWidth
            />
          </Paper>

          {/* ScoreForm — same as the standard scoring screen */}
          <ScoreForm
            stage={stageForForm}
            scoreData={scoreData}
            onChange={setScoreData}
          />

          {/* Penalties */}
          <Typography variant="h6" sx={{ textAlign: 'center', mb: 1, mt: 2 }}>{t('app.qsPenalties')}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <PlusMinus
              value={scorePenalties}
              onChange={setScorePenalties}
              min={0} max={99} size="large"
            />
          </Box>

          {/* Live preview */}
          {liveResult && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6">{liveResult.total}</Typography>
                  <Typography variant="caption">{t('app.qsTotal')}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6">{liveResult.timeStr}</Typography>
                  <Typography variant="caption">{t('app.qsTime')}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6">{liveResult.factorStr}</Typography>
                  <Typography variant="caption">{t('app.qsFactor')}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, mt: 1, justifyContent: 'center' }}>
                <Typography variant="caption">{t('app.qsAlphas')}: {liveResult.alphas}</Typography>
                <Typography variant="caption">{t('app.qsCharlies')}: {liveResult.charlies}</Typography>
                <Typography variant="caption">{t('app.qsDeltas')}: {liveResult.deltas}</Typography>
                <Typography variant="caption">{t('app.qsMisses')}: {liveResult.misses}</Typography>
              </Box>
            </Paper>
          )}

          {/* Save score */}
          <Button
            variant="contained"
            color="primary"
            onClick={onSaveScore}
            fullWidth
            sx={{ mt: 2, mb: 2 }}
          >
            {t('app.qsSave')}
          </Button>
        </Box>
      </Box>
    );
  }

  // ==================== VIEW MODE ====================
  const scores = stageData.scores || [];
  const bestHf = scores.reduce((best, s) => {
    if (!s.score) return best;
    const r = summarizeScore({ score: s.score, powerFactor: 'minor', stage: stageForForm });
    return r.factor > best ? r.factor : best;
  }, 0);

  return (
    <Box sx={{ pb: '80px', bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Hero header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
        color: 'white',
        px: 2, pt: 1.5, pb: 3,
        position: 'relative',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <IconButton onClick={() => navigate('/app/quickscore/' + mIdx)} sx={{ color: 'white', mr: 1 }}>
            <BackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              {matchData.title}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {stageData.title}
            </Typography>
          </Box>
          <IconButton onClick={onDeleteStage} sx={{ color: 'rgba(255,255,255,0.7)' }}>
            <Delete />
          </IconButton>
        </Box>

        {/* Stage info chips */}
        <Box sx={{ display: 'flex', gap: 1.5, ml: 6 }}>
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, px: 1.5, py: 0.5,
            backdropFilter: 'blur(4px)',
          }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', lineHeight: 1 }}>
              {t('app.qsTargets')}
            </Typography>
            <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 700 }}>
              {curPaper}
            </Typography>
          </Box>
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, px: 1.5, py: 0.5,
            backdropFilter: 'blur(4px)',
          }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', lineHeight: 1 }}>
              {t('app.qsPoppers')}
            </Typography>
            <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 700 }}>
              {curPoppers}
            </Typography>
          </Box>
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, px: 1.5, py: 0.5,
            backdropFilter: 'blur(4px)',
          }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', lineHeight: 1 }}>
              {t('app.qsScores')}
            </Typography>
            <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 700 }}>
              {scores.length}
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={() => {
              setPaper(curPaper);
              setPoppers(curPoppers);
              setStageName(stageData.title);
              setEditMode(true);
            }}
            sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none', minWidth: 0, ml: 'auto' }}
          >
            {t('common.edit')}
          </Button>
        </Box>
      </Box>

      {/* Score cards */}
      <Box sx={{ px: 1.5, mt: -1.5 }}>
        {scores.length === 0 ? (
          <Paper sx={{
            textAlign: 'center', py: 5, borderRadius: 3,
            background: 'white',
          }}>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
              {t('app.qsNoScore')}
            </Typography>
            <Fab color="primary" onClick={onNewScore} sx={{ boxShadow: 3 }}>
              <Add />
            </Fab>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {scores.map((s, scoreIdx) => {
              const result = s.score ? summarizeScore({
                score: s.score, powerFactor: 'minor', stage: stageForForm,
              }) : null;
              const isTop = result && bestHf > 0 && result.factor >= bestHf - 0.0001;

              return (
                <Paper key={s.id} sx={{
                  borderRadius: 2.5, overflow: 'hidden',
                  border: isTop ? '2px solid' : '1px solid',
                  borderColor: isTop ? 'primary.main' : 'divider',
                  position: 'relative',
                }}>
                  {isTop && scores.length > 1 && (
                    <Box sx={{
                      position: 'absolute', top: 0, right: 0,
                      bgcolor: 'primary.main', color: 'white',
                      px: 1, py: 0.25, borderBottomLeftRadius: 8,
                      fontSize: 11, fontWeight: 700,
                    }}>
                      #1
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5 }}>
                    {/* Rank circle */}
                    <Box sx={{
                      width: 36, height: 36, borderRadius: '50%',
                      bgcolor: isTop ? 'primary.main' : grey[200],
                      color: isTop ? 'white' : 'text.secondary',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: 14, mr: 1.5, flexShrink: 0,
                    }}>
                      {scoreIdx + 1}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                        {s.title}
                      </Typography>
                      {result && (
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {result.alphas}A {result.charlies}C {result.deltas}D {result.misses}M
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    {result && (
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            {result.factorStr}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
                            HF
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {result.total}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
                            pts
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {result.timeStr}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
                            sec
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    <IconButton size="small" onClick={() => onDeleteScore(scoreIdx)} sx={{ ml: 0.5, color: grey[400] }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              );
            })}

            {/* Add score button */}
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={onNewScore}
              sx={{
                borderRadius: 2.5, py: 1.5, mt: 0.5,
                textTransform: 'none', fontWeight: 600,
              }}
            >
              {t('app.qsNewScore')}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
